import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy01, Play } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppSelect } from "@/components/app/AppSelect";
import { TokenPicker } from "@/components/app/TokenPicker";
import {
  buildAsymApproveRequest,
  buildAsymRequest,
  decryptAsymMessage,
  signAsymMessage,
  type AsymNextCall,
  type AsymToolEnvelope,
} from "@/lib/asymTools";
import {
  ASYM_GROUP_ORDER,
  ASYM_OPERATIONS,
  ASYM_TOOLS,
  asymGroupLabelKey,
  buildAsymBody,
  defaultValues,
  findAsymTool,
  isComplete,
  visibleFields,
  type AsymFieldSpec,
  type AsymToolId,
  type AsymToolSpec,
  type AsymValues,
} from "./asymTools/catalog";
import { Field, inputCls } from "./shared";

/**
 * Vydá asym request, podepíše zprávu od auth serveru, obojí najednou, nebo zprávu dešifruje.
 *
 * Server nic **neodesílá** — je to generátor pro integraci, která si volání pošle vlastní
 * cestou. Stránka proto volá **zákaznické** `/api/v1/asym/**` (ne admin dvojče,
 * to zaniklo): testuje se kontrakt, který dostane integrátor.
 *
 * Formulář je vykreslený z katalogu; podmíněnost polí je deklarativní (`showWhen`), takže
 * runner nemá o jednotlivých polích ponětí.
 */
export function AsymToolsPage() {
  const { t } = useTranslation();

  const [toolId, setToolId] = useState<AsymToolId>("request");
  const tool = findAsymTool(toolId);

  const [values, setValues] = useState<AsymValues>(() => defaultValues(findAsymTool("request")));
  const [envelope, setEnvelope] = useState<AsymToolEnvelope | null>(null);
  const [running, setRunning] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const fields = visibleFields(tool, values);
  const canRun = isComplete(tool, values) && !running;

  const switchTool = (next: AsymToolId) => {
    setToolId(next);
    // Token a zpráva se mezi nástroji přenášejí — je to tentýž token a tatáž zpráva,
    // opisovat je při každém přepnutí by z nástroje udělalo překážku.
    setValues((prev) => ({ ...defaultValues(findAsymTool(next)), ...carryOver(prev) }));
    setEnvelope(null);
    setCopied(null);
  };

  const set = (name: string, value: string) => setValues((prev) => ({ ...prev, [name]: value }));

  const run = async () => {
    setRunning(true);
    setCopied(null);
    try {
      setEnvelope(await submit(tool, values));
    } finally {
      setRunning(false);
    }
  };

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
    } catch {
      // Bez zabezpečeného kontextu schránka není; pole zůstává vybíratelné ručně.
      setCopied(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("asymtools.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("asymtools.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label={t("asymtools.tool")}>
        {ASYM_TOOLS.map((spec) => (
          <button
            key={spec.id}
            type="button"
            role="tab"
            aria-selected={spec.id === toolId}
            onClick={() => switchTool(spec.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ring-1 ${
              spec.id === toolId
                ? "bg-brand-primary text-white ring-transparent"
                : "bg-primary text-secondary ring-secondary hover:bg-secondary"
            }`}
          >
            {t(spec.label)}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
        <div>
          <p className="text-sm text-secondary">{t(tool.hint)}</p>
          <p className="font-mono text-xs text-tertiary">{tool.endpoint}</p>
        </div>

        {fields.map((field) => (
          <FieldRenderer key={field.name} field={field} values={values} onChange={set} />
        ))}

        <div>
          <Button size="sm" iconLeading={Play} isDisabled={!canRun} onPress={() => void run()}>
            {running ? t("asymtools.running") : t(tool.submit)}
          </Button>
        </div>
      </div>

      {envelope && <EnvelopeView envelope={envelope} copied={copied} onCopy={copy} />}
    </div>
  );
}

/**
 * Hodnoty, které dávají smysl i po přepnutí nástroje. Scoring a volba hesla se záměrně
 * nepřenášejí — patří ke konkrétnímu podpisu a tiše zděděná hodnota by změnila výsledek.
 */
function carryOver(values: AsymValues): AsymValues {
  const kept: AsymValues = {};
  if (values.tokenId) kept.tokenId = values.tokenId;
  if (values.message) kept.message = values.message;
  return kept;
}

function submit(tool: AsymToolSpec, values: AsymValues): Promise<AsymToolEnvelope> {
  const body = buildAsymBody(tool, values);
  switch (tool.id) {
    case "request":
      return buildAsymRequest(body);
    case "approve":
      return buildAsymApproveRequest(body);
    case "signature":
      return signAsymMessage(body);
    case "decrypt":
      return decryptAsymMessage(body);
  }
}

function FieldRenderer({
  field,
  values,
  onChange,
}: {
  field: AsymFieldSpec;
  values: AsymValues;
  onChange: (name: string, value: string) => void;
}) {
  const { t } = useTranslation();
  const value = values[field.name] ?? "";
  const label = t(field.label);
  const hint = field.hint ? t(field.hint) : undefined;

  if (field.kind === "tokenSelect") {
    return (
      <Field label={label} hint={hint}>
        <TokenPicker
          value={value}
          onChange={(v) => onChange(field.name, v)}
          activeOnly
          placeholder={t("asymtools.tokenPlaceholder")}
        />
      </Field>
    );
  }

  if (field.kind === "operationSelect") {
    const grouped = ASYM_GROUP_ORDER.map((group) => ({
      group,
      operations: ASYM_OPERATIONS.filter((op) => op.group === group),
    })).filter((g) => g.operations.length > 0);

    return (
      <Field label={label} hint={hint}>
        <AppSelect aria-label={label} value={value} onChange={(e) => onChange(field.name, e.target.value)}>
          {grouped.map((g) => (
            <optgroup key={g.group} label={t(asymGroupLabelKey(g.group))}>
              {g.operations.map((op) => (
                <option key={op.id} value={op.id}>
                  {t(op.labelKey)}
                </option>
              ))}
            </optgroup>
          ))}
        </AppSelect>
      </Field>
    );
  }

  if (field.kind === "select") {
    return (
      <Field label={label} hint={hint}>
        <AppSelect aria-label={label} value={value} onChange={(e) => onChange(field.name, e.target.value)}>
          {(field.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.label)}
            </option>
          ))}
        </AppSelect>
      </Field>
    );
  }

  if (field.kind === "passwordRadio") {
    return (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm font-medium text-secondary">{label}</legend>
        <div className="flex gap-4">
          {(["valid", "invalid"] as const).map((choice) => (
            <label key={choice} className="flex items-center gap-2 text-sm text-secondary">
              <input
                type="radio"
                name={field.name}
                checked={(value || "valid") === choice}
                onChange={() => onChange(field.name, choice)}
              />
              {t(choice === "valid" ? "asymtools.sign.passwordValid" : "asymtools.sign.passwordInvalid")}
            </label>
          ))}
        </div>
        {hint && <span className="text-xs text-tertiary">{hint}</span>}
      </fieldset>
    );
  }

  if (field.kind === "textarea") {
    return (
      <Field label={label} hint={hint}>
        <textarea
          rows={4}
          className={`${inputCls} ${field.mono ? "font-mono text-xs" : ""}`}
          value={value}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      </Field>
    );
  }

  return (
    <Field label={label} hint={hint}>
      <input
        className={inputCls}
        inputMode={field.kind === "number" ? "numeric" : undefined}
        value={value}
        onChange={(e) => onChange(field.name, e.target.value)}
      />
    </Field>
  );
}

/**
 * Vykreslení obálky podle diskriminátoru — stejná struktura, jakou má panel KB CAAS
 * workflow. `nextCall` je tu to podstatné: bez něj by operátor viděl hodnotu, ale nevěděl,
 * do kterého volání patří.
 */
function EnvelopeView({
  envelope,
  copied,
  onCopy,
}: {
  envelope: AsymToolEnvelope;
  copied: string | null;
  onCopy: (label: string, value: string) => void;
}) {
  const { t } = useTranslation();
  const { status, result, nextCall, error } = envelope;

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <div className="flex flex-wrap items-center gap-2">
        <Badge type="pill-color" size="sm" color={status === "COMPLETED" ? "success" : "error"}>
          {status}
        </Badge>
      </div>

      {error && (
        <div role="alert" className="rounded-md bg-secondary p-3 text-sm ring-1 ring-error">
          {error.code && <p className="font-semibold text-error-primary">{error.code}</p>}
          {error.message && <p className="text-secondary">{error.message}</p>}
          {error.recoveryHint && <p className="text-xs text-tertiary">{error.recoveryHint}</p>}
        </div>
      )}


      {result != null && <ResultValues result={result as Record<string, unknown>} copied={copied} onCopy={onCopy} />}

      {nextCall && <NextCallView call={nextCall} />}

      {result != null && <p className="text-xs text-tertiary">{t("asymtools.notSentHint")}</p>}
    </div>
  );
}

/** Dlouhé hodnoty (blob, podpis) jako kopírovatelná pole, zbytek jako řádky. */
function ResultValues({
  result,
  copied,
  onCopy,
}: {
  result: Record<string, unknown>;
  copied: string | null;
  onCopy: (label: string, value: string) => void;
}) {
  const { t } = useTranslation();
  const entries = useMemo(
    () => Object.entries(result).filter(([key]) => key !== "tokenId"),
    [result],
  );

  return (
    <div className="flex flex-col gap-3">
      {entries.map(([key, raw]) => {
        const value = typeof raw === "string" ? raw : String(raw);
        const long = value.length > 40;
        return (
          <div key={key} className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-secondary">{key}</span>
              {long && (
                <Button size="sm" color="link-gray" iconLeading={Copy01} onPress={() => onCopy(key, value)}>
                  {copied === key ? t("asymtools.copied") : t("asymtools.copy")}
                </Button>
              )}
            </div>
            {long ? (
              <textarea
                readOnly
                rows={3}
                aria-label={key}
                className={`${inputCls} font-mono text-xs`}
                value={value}
              />
            ) : (
              <input readOnly aria-label={key} className={`${inputCls} font-mono text-xs`} value={value} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Kam vydaná hodnota patří na drátě — včetně dalších volání, která berou tutéž trojici. */
function NextCallView({ call, nested = false }: { call: AsymNextCall; nested?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={`flex flex-col gap-2 rounded-md bg-secondary p-3 text-xs ring-1 ring-secondary ${nested ? "ml-3" : ""}`}>
      {!nested && <h3 className="text-sm font-semibold text-primary">{t("asymtools.nextCall")}</h3>}
      <p className="font-mono break-all text-secondary">
        {call.method} {call.endpoint}
      </p>
      {call.what && <p className="text-tertiary">{call.what}</p>}
      {call.query && Object.keys(call.query).length > 0 && (
        <KeyValues title={t("asymtools.nextCall.query")} data={call.query} />
      )}
      {call.payloadSchema && Object.keys(call.payloadSchema).length > 0 && (
        <KeyValues title={t("asymtools.nextCall.body")} data={call.payloadSchema} />
      )}
      {call.hint && <p className="text-tertiary">{call.hint}</p>}
      {call.alternatives && call.alternatives.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="font-semibold text-tertiary">{t("asymtools.nextCall.alternatives")}</p>
          {call.alternatives.map((alt, i) => (
            <NextCallView key={i} call={alt} nested />
          ))}
        </div>
      )}
    </div>
  );
}

function KeyValues({ title, data }: { title: string; data: Record<string, string> }) {
  return (
    <div>
      <p className="mb-1 font-semibold text-tertiary">{title}</p>
      <dl className="flex flex-col gap-0.5">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex gap-2">
            <dt className="font-mono text-tertiary">{key}</dt>
            <dd className="font-mono break-all text-secondary">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default AsymToolsPage;
